<template>
	<div class="crRoot">
		<h1 class="pageHeading">Code Review</h1>
		<nav class="tabs" aria-label="Code review pages">
			<router-link :to="{ name: 'codeReviewQueue' }">Findings</router-link>
			<router-link :to="{ name: 'codeReviewPushes' }" class="active">Reviewed pushes</router-link>
			<router-link :to="{ name: 'codeReviewFixes' }">Fixes</router-link>
			<router-link :to="{ name: 'codeReviewReport' }">Report</router-link>
		</nav>

		<div class="card">
			<form class="row" @submit.prevent="load">
				<label for="crpStatus">Status</label>
				<select id="crpStatus" v-model="status" @change="filterChanged">
					<option value="">All</option>
					<option v-for="s in targetStatuses" :key="s" :value="s">{{label(s)}}</option>
				</select>
				<label for="crpRepo">Repository</label>
				<select id="crpRepo" v-model="repo" @change="filterChanged">
					<option value="">All</option>
					<option v-for="r in repoOptions" :key="r" :value="r">{{r}}</option>
				</select>
				<button type="submit" :disabled="loading">Refresh</button>
				<span v-if="loading" class="loadingInline"><ScaleLoader /> Loading…</span>
				<span v-else class="hintText" role="status">{{targets.length}} push{{targets.length === 1 ? '' : 'es'}}{{truncated ? ' (only the newest ' + limit + ' are shown)' : ''}}</span>
			</form>
		</div>

		<div v-if="error" class="errorBox">{{error}}</div>
		<div class="card tableCard">
			<table v-if="targets.length" class="dense">
				<thead>
					<tr>
						<th scope="col">Push</th>
						<th scope="col">Pushed</th>
						<th scope="col">Pusher</th>
						<th scope="col">Status</th>
						<th scope="col">Findings</th>
						<th scope="col">Notified</th>
						<th scope="col">Review run</th>
						<th scope="col">Triage run</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="t in targets" :key="t.id" class="clickable" :class="{ attention: t.status === 'Failed' || t.status === 'Safety' }" @click="open(t, $event)">
						<td class="nowrap"><router-link :to="pushRoute(t)">{{t.repo}} {{t.pushId}}</router-link></td>
						<td class="nowrap">{{formatTime(t.pushedAt)}}</td>
						<td>{{t.pusher}}</td>
						<td class="statusCell"><span class="badge" :class="statusClass(t.status)">{{label(t.status)}}</span><div v-if="t.statusReason" class="reasonText">{{t.statusReason}}</div></td>
						<td>{{t.findingCount}}</td>
						<td class="nowrap">{{t.notifiedAt ? formatTime(t.notifiedAt) : ''}}</td>
						<td class="runCell"><template v-if="t.reviewRun">{{runText(t.reviewRun)}}<div class="hintText">{{t.reviewRun.model}}</div></template></td>
						<td class="runCell"><template v-if="t.triageRun">{{runText(t.triageRun)}}<div class="hintText">{{t.triageRun.model}}</div></template></td>
					</tr>
				</tbody>
			</table>
			<div v-else-if="!loading && !error" class="hintText">No reviewed pushes match.</div>
		</div>
	</div>
</template>

<script>
	import { GetSummary, ListTargets } from 'appRoot/api/CodeReviewData';
	import { targetStatuses, label, statusClass, formatTime, formatCost, formatDurationMs, loadStored, saveStored } from './codeReviewFormat';

	const storageKey = 'codeReview_pushFilters';

	export default {
		data()
		{
			return {
				targetStatuses,
				status: '',
				repo: '',
				repos: [],
				targets: [],
				truncated: false,
				limit: 0,
				loading: false,
				error: null,
				loadToken: 0
			};
		},
		created()
		{
			let q = this.$route.query || {};
			let stored = loadStored(storageKey);
			if (q.status === undefined && q.repo === undefined && stored && typeof stored === 'object')
			{
				// Opened without filters in the address (e.g. from the tabs): restore the filters used last time.
				this.status = typeof stored.status === 'string' && targetStatuses.indexOf(stored.status) !== -1 ? stored.status : '';
				this.repo = typeof stored.repo === 'string' ? stored.repo : '';
				if (this.status || this.repo)
					this.filterChanged();
			}
			else
				this.applyQuery();
			this.loadRepos();
			this.load();
		},
		computed:
		{
			repoOptions()
			{
				let list = this.repos.slice();
				if (this.repo && list.indexOf(this.repo) === -1)
					list.push(this.repo);
				return list;
			}
		},
		watch:
		{
			'$route.query'()
			{
				if (this.$route.name !== 'codeReviewPushes')
					return;
				if (this.applyQuery())
					this.load();
			}
		},
		methods:
		{
			label,
			statusClass,
			formatTime,
			/** Takes the status and repository filters from the query string.  Returns true if they changed. */
			applyQuery()
			{
				let q = this.$route.query || {};
				let status = typeof q.status === 'string' && targetStatuses.indexOf(q.status) !== -1 ? q.status : '';
				let repo = typeof q.repo === 'string' ? q.repo : '';
				let changed = status !== this.status || repo !== this.repo;
				this.status = status;
				this.repo = repo;
				return changed;
			},
			pushRoute(t)
			{
				return { name: 'codeReviewPush', params: { id: t.id } };
			},
			open(t, e)
			{
				if (e && e.target && e.target.closest && e.target.closest('a'))
					return;
				this.$router.push(this.pushRoute(t));
			},
			runText(run)
			{
				if (!run)
					return '';
				return label(run.status) + ' · ' + formatCost(run.costUsd) + ' · ' + formatDurationMs(run.durationMs);
			},
			filterChanged()
			{
				let query = {};
				if (this.status)
					query.status = this.status;
				if (this.repo)
					query.repo = this.repo;
				this.$router.replace({ name: 'codeReviewPushes', query: query });
			},
			async loadRepos()
			{
				try
				{
					let data = await GetSummary();
					if (data.success)
						this.repos = data.repos;
				}
				catch (ex)
				{
				}
			},
			async load()
			{
				let token = ++this.loadToken;
				this.loading = true;
				this.error = null;
				saveStored(storageKey, { status: this.status, repo: this.repo });
				try
				{
					let data = await ListTargets({ status: this.status || null, repo: this.repo || null });
					if (token !== this.loadToken)
						return;
					if (data.success)
					{
						this.targets = data.targets;
						this.truncated = data.truncated;
						this.limit = data.limit;
					}
					else
					{
						this.targets = [];
						this.error = data.error;
					}
				}
				catch (ex)
				{
					if (token === this.loadToken)
						this.error = ex.message;
				}
				finally
				{
					if (token === this.loadToken)
						this.loading = false;
				}
			}
		}
	};
</script>

<style scoped src="./codeReview.css"></style>
<style scoped>
	.tableCard
	{
		overflow-x: auto;
	}

	.dense tbody tr.attention
	{
		background-color: #FFF0F0;
	}

	.statusCell
	{
		min-width: 200px;
		max-width: 420px;
	}

	.reasonText
	{
		font-size: 12px;
		color: #444444;
		word-break: break-word;
		margin-top: 2px;
	}

	.runCell
	{
		min-width: 120px;
	}
</style>
